import { z } from 'zod';

/**
 * PretentionDraft — la PRÉTENTION au sens des art. 4, 768 et 954 CPC : ce que le
 * client DEMANDE au juge, formulée comme au dispositif (« Condamner X à payer… »,
 * « Prononcer la nullité… »). C'est l'unité structurante de l'acte : la DISCUSSION
 * s'organise par prétention (chacune fondée par ≥ 1 moyen), et le PAR CES MOTIFS
 * récapitule EXACTEMENT les prétentions — art. 954 CPC : la cour ne statue que sur
 * les prétentions énoncées au dispositif.
 *
 * Chantier « prétentions » (2026-07-06) : ce niveau est introduit AU-DESSUS des
 * `MoyenDraft` (ex-`ArgumentDraft`) qui, eux, portent le raisonnement
 * (majeure/mineure + fondements). Hiérarchie : Prétention → Moyens (rang
 * principal/subsidiaire) → jurisprudences/pièces.
 */

/**
 * Rang d'une prétention dans la hiérarchie de subsidiarité du dispositif :
 * « à titre principal » / « à titre subsidiaire » / « à titre infiniment
 * subsidiaire ». La subsidiarité relie des PRÉTENTIONS entre elles (ex. à titre
 * principal la nullité, à titre subsidiaire la résolution) — distinct du rang des
 * moyens AU SEIN d'une même prétention (`MoyenRang`).
 */
export const PretentionRangSchema = z.enum([
  'principal',
  'subsidiaire',
  'infiniment_subsidiaire',
]);
export type PretentionRang = z.infer<typeof PretentionRangSchema>;

/**
 * `principale` = prétention de fond (le cœur du litige), qui devrait être fondée par ≥ 1
 * moyen ; sans moyen, elle est acceptée et signalée (`findUncoveredFondPretentionIds`).
 * `accessoire` = demande formée quel que soit le sort des demandes de fond, sans
 * moyen dédié : frais irrépétibles (art. 700 CPC / L.761-1 CJA), dépens, exécution
 * provisoire. Rendue sous « En tout état de cause » au dispositif ; une demande qui
 * suit le sort d'une autre (injonction après annulation) reste donc `principale`,
 * fondée par les moyens de celle-ci (règle 4 du prompt du planner).
 */
export const PretentionCategorieSchema = z.enum(['principale', 'accessoire']);
export type PretentionCategorie = z.infer<typeof PretentionCategorieSchema>;

export const PretentionDraftSchema = z.object({
  id: z.string(),                                  // 'pret-1', 'pret-2', ...
  /** Formulation type dispositif — verbe à l'infinitif, chiffrée si possible. */
  objet: z.string(),
  /** Montant/mesure chiffrée EXTRAITE du dossier (« 45 000 € »), null sinon — jamais inventé. */
  quantum: z.string().nullable(),
  rang: PretentionRangSchema,
  /** Id de la prétention à laquelle celle-ci est subsidiaire (null si autonome/principale). */
  subsidiaire_de: z.string().nullable(),
  categorie: PretentionCategorieSchema,
});                                                // non-strict : sortie LLM (planner) — cf. moyen-draft.ts + CLAUDE.md
export type PretentionDraft = z.infer<typeof PretentionDraftSchema>;

/**
 * Shape structurel minimal d'un moyen pour la validation d'arbre — évite le
 * couplage au schéma complet `MoyenDraftSchema` (pas d'import croisé).
 */
export interface PretentionTreeMoyen {
  id: string;
  pretention_ids?: string[] | undefined;
}

/**
 * Valeurs textuelles que les LLM émettent au lieu du `null` JSON pour un champ
 * nullable — cause d'un incident planner (2026-07-06) : DeepSeek V4-Pro renvoyait
 * la CHAÎNE `"null"` pour `subsidiaire_de`, que `validatePretentionTree` prenait
 * pour une vraie référence introuvable → rejet de tout le plan → retry-storm de
 * la chaîne de modèles → génération lente puis échouée. Comparaison trim + minuscule.
 */
const SUBSIDIAIRE_DE_NULL_SENTINELS = new Set<string>([
  '', 'null', 'none', 'nil', 'aucun', 'aucune', 'undefined', 'n/a', 'na', '-', '—',
]);

function coerceSubsidiaireDe(raw: string | null): string | null {
  if (raw === null) return null;
  return SUBSIDIAIRE_DE_NULL_SENTINELS.has(raw.trim().toLowerCase()) ? null : raw.trim();
}

/**
 * Répare DÉTERMINISTIQUEMENT le graphe prétentions ↔ moyens AVANT validation, pour
 * absorber le bruit LLM cosmétique sans déclencher le retry-storm du planner. Ne
 * fabrique JAMAIS de rattachement (ça reste une décision juridique) — corrige
 * seulement des incohérences mécaniques que le dispositif ignore de toute façon
 * (il se groupe sur `rang`, pas sur `subsidiaire_de`) :
 *  - `subsidiaire_de` valant une sentinelle textuelle (`"null"`, `""`, `"aucun"`…) → `null` ;
 *  - `subsidiaire_de` forcé à `null` si `rang === 'principal'`, si auto-référent, ou si
 *    la référence n'existe pas (le LLM met souvent `rang:"principal"` partout + une ref bidon) ;
 *  - `pretention_ids` d'un moyen : refs vers des prétentions inexistantes RETIRÉES
 *    (mais on ne rattache rien — un moyen vidé de toutes ses refs restera signalé
 *    par `validatePretentionTree`, erreur STRUCTURELLE légitime).
 *
 * Générique pour préserver le type concret des entrées (`MoyenDraft`, `PlannerMoyen`…).
 * @returns objets réparés (nouveaux si modifiés, mêmes références sinon) + `repairs`
 *   (journal lisible des corrections, pour log info — jamais une erreur dure).
 */
export function normalizePretentionTree<
  P extends PretentionDraft,
  M extends PretentionTreeMoyen,
>(
  pretentions: readonly P[],
  moyens: readonly M[],
): { pretentions: P[]; moyens: M[]; repairs: string[] } {
  const repairs: string[] = [];
  const validIds = new Set(pretentions.map((p) => p.id));

  const repairedPretentions = pretentions.map((p) => {
    const coerced = coerceSubsidiaireDe(p.subsidiaire_de);
    let next = coerced;
    if (next !== null) {
      if (p.rang === 'principal') {
        repairs.push(`pretentions: '${p.id}' rang='principal' → subsidiaire_de='${next}' forcé à null (une principale n'est subsidiaire de rien).`);
        next = null;
      } else if (next === p.id) {
        repairs.push(`pretentions: '${p.id}' subsidiaire_de auto-référent → forcé à null.`);
        next = null;
      } else if (!validIds.has(next)) {
        repairs.push(`pretentions: '${p.id}' subsidiaire_de='${next}' inexistant → forcé à null.`);
        next = null;
      }
    } else if (coerced === null && p.subsidiaire_de !== null) {
      repairs.push(`pretentions: '${p.id}' subsidiaire_de='${p.subsidiaire_de}' coercé en null (valeur sentinelle).`);
    }
    if (next === p.subsidiaire_de) return p;
    const clone = { ...p };
    clone.subsidiaire_de = next;
    return clone;
  });

  const repairedMoyens = moyens.map((m) => {
    const refs = m.pretention_ids;
    if (!refs || refs.length === 0) return m;
    const filtered = refs.filter((ref) => validIds.has(ref));
    if (filtered.length === refs.length) return m;
    const removed = refs.filter((ref) => !validIds.has(ref));
    repairs.push(`moyens: '${m.id}' référence(s) de prétention inexistante(s) retirée(s): ${removed.join(', ')}.`);
    const clone = { ...m };
    clone.pretention_ids = filtered;
    return clone;
  });

  return { pretentions: repairedPretentions, moyens: repairedMoyens, repairs };
}

function normalizeObjetForDuplicate(objet: string): string {
  return objet.normalize('NFC').toLowerCase().replace(/\s+/g, ' ').replace(/[\s.;,]+$/u, '').trim();
}

/**
 * Valide la cohérence du graphe prétentions ↔ moyens. Retourne la liste des
 * erreurs (vide = arbre valide). Messages précis et actionnables : ils sont
 * réinjectés tels quels dans le retry LLM du planner (et lus hors contexte dans
 * les logs), cf. règle « error messages written for LLMs first ».
 *
 * ⚠️ À appeler APRÈS `normalizePretentionTree` (qui absorbe le bruit cosmétique
 * `subsidiaire_de`) — sinon un simple `"null"` textuel du LLM déclenche un rejet
 * et le retry-storm du planner (incident 2026-07-06).
 *
 * Invariants vérifiés :
 *  1. ≥ 1 prétention ; ids uniques ; ≥ 1 prétention de fond (`categorie='principale'`)
 *     de rang `principal`.
 *  2. `subsidiaire_de` : jamais auto-référent, référence un id existant, et une
 *     prétention de rang `principal` n'a pas de parent de subsidiarité.
 *  3. Moyens : ids uniques ; `pretention_ids` non vide ; chaque référence existe.
 *  4. Une même demande (objet identique à la casse, aux espaces et à la ponctuation
 *     finale près) ne s'écrit qu'une fois : le dispositif la répéterait (prod
 *     2026-10-23, frais irrépétibles et dépens écrits trois fois chacun).
 *
 * Une prétention de fond sans moyen n'est PAS une erreur ici : elle se lit avec
 * `findUncoveredFondPretentionIds`. En faire une erreur rejetait le plan entier sur
 * toute la chaîne de modèles (prod 2026-10-19 → 10-23 : injonctions L.911-1 CJA
 * rangées en fond, ou plus de demandes que les 5 moyens permis), et l'avocat
 * n'obtenait ni plan ni recherche. L'aval sait déjà signaler une telle prétention
 * (prélude « fondée par AUCUN moyen », santé `en_peril`, warning de couverture).
 */
export function validatePretentionTree(
  pretentions: readonly PretentionDraft[],
  moyens: readonly PretentionTreeMoyen[],
): string[] {
  const errors: string[] = [];

  if (pretentions.length === 0) {
    errors.push('pretentions est vide : le plan doit formuler au moins une prétention (art. 4 CPC).');
    return errors;
  }

  const pretentionIds = new Set<string>();
  for (const p of pretentions) {
    if (pretentionIds.has(p.id)) {
      errors.push(`pretentions: id dupliqué '${p.id}' — chaque prétention doit avoir un id unique.`);
    }
    pretentionIds.add(p.id);
  }

  // Positions (1-based) et non ids : l'id est une chaîne libre du modèle, et ce
  // message finit dans les logs ordinaires.
  const firstPositionByObjet = new Map<string, number>();
  pretentions.forEach((p, index) => {
    const key = normalizeObjetForDuplicate(p.objet);
    const first = firstPositionByObjet.get(key);
    if (first === undefined) {
      firstPositionByObjet.set(key, index + 1);
    } else {
      errors.push(
        `pretentions: la prétention n°${index + 1} répète la demande de la n°${first} (même objet) — chaque demande ne s'écrit qu'une fois au dispositif.`,
      );
    }
  });

  if (!pretentions.some((p) => p.categorie === 'principale' && p.rang === 'principal')) {
    errors.push(
      "pretentions: aucune prétention de fond à titre principal (categorie='principale' + rang='principal') — un plan ne peut pas être uniquement subsidiaire ou accessoire.",
    );
  }

  for (const p of pretentions) {
    if (p.subsidiaire_de !== null) {
      if (p.subsidiaire_de === p.id) {
        errors.push(`pretentions: '${p.id}' est subsidiaire d'elle-même (subsidiaire_de='${p.id}').`);
      } else if (!pretentionIds.has(p.subsidiaire_de)) {
        errors.push(
          `pretentions: '${p.id}' référence subsidiaire_de='${p.subsidiaire_de}' qui n'existe pas (ids valides: ${[...pretentionIds].join(', ')}).`,
        );
      }
      if (p.rang === 'principal') {
        errors.push(
          `pretentions: '${p.id}' est rang='principal' mais porte subsidiaire_de='${p.subsidiaire_de}' — une prétention principale n'est subsidiaire de rien (mettre subsidiaire_de=null ou rang='subsidiaire').`,
        );
      }
    }
  }

  const moyenIds = new Set<string>();
  for (const m of moyens) {
    if (moyenIds.has(m.id)) {
      errors.push(`moyens: id dupliqué '${m.id}' — chaque moyen doit avoir un id unique.`);
    }
    moyenIds.add(m.id);

    const refs = m.pretention_ids ?? [];
    if (refs.length === 0) {
      errors.push(
        `moyens: '${m.id}' n'est rattaché à aucune prétention (pretention_ids vide) — un moyen fonde toujours au moins une prétention.`,
      );
      continue;
    }
    for (const ref of refs) {
      if (!pretentionIds.has(ref)) {
        errors.push(
          `moyens: '${m.id}' référence la prétention inexistante '${ref}' (ids valides: ${[...pretentionIds].join(', ')}).`,
        );
      }
    }
  }

  return errors;
}

/**
 * Ids des prétentions de fond qu'aucun moyen ne fonde (art. 768/954 CPC : vouées
 * au rejet en l'état). Les `accessoire` sont exemptées.
 */
export function findUncoveredFondPretentionIds(
  pretentions: readonly PretentionDraft[],
  moyens: readonly PretentionTreeMoyen[],
): string[] {
  const covered = new Set(moyens.flatMap((m) => m.pretention_ids ?? []));
  return pretentions
    .filter((p) => p.categorie === 'principale' && !covered.has(p.id))
    .map((p) => p.id);
}

/**
 * Warning de couverture d'une prétention — calculé DÉTERMINISTIQUEMENT (pas par
 * LLM) après le refiner (Phase 2 du chantier prétentions). Distinct des ERREURS
 * de `validatePretentionTree` : un warning n'invalide pas le plan, il signale à
 * l'avocat une fragilité structurelle (concentration des moyens insuffisante).
 */
export interface PretentionCoverageWarning {
  pretention_id: string;
  code: 'pretention_sans_moyen' | 'moyen_unique_fragile';
  detail: string;
}

/** Shape structurel minimal d'un moyen pour l'analyse de couverture. */
export interface PretentionCoverageMoyen {
  id: string;
  pretention_ids?: string[] | undefined;
  confidence?: number | undefined;
}

/** En-dessous de cette confidence, un moyen UNIQUE sur une prétention de fond est signalé. */
const FRAGILE_CONFIDENCE_THRESHOLD = 0.6;

/**
 * Analyse la couverture des prétentions de fond par les moyens. Retourne des
 * warnings (jamais d'erreur) :
 *  - `pretention_sans_moyen` : prétention de fond sans aucun moyen, acceptée dès
 *    le planner (cf. `findUncoveredFondPretentionIds`) ;
 *  - `moyen_unique_fragile` : prétention de fond défendue par UN SEUL moyen dont
 *    `confidence < 0.6` — tout repose sur un terrain incertain (Cesareo : un
 *    moyen subsidiaire sur un autre terrain serait bienvenu).
 * Les prétentions `accessoire` sont exemptées (pas de moyen attendu).
 */
export function computePretentionCoverageWarnings(
  pretentions: readonly PretentionDraft[],
  moyens: readonly PretentionCoverageMoyen[],
): PretentionCoverageWarning[] {
  const warnings: PretentionCoverageWarning[] = [];

  for (const p of pretentions) {
    if (p.categorie !== 'principale') continue;
    const attached = moyens.filter((m) => (m.pretention_ids ?? []).includes(p.id));

    if (attached.length === 0) {
      warnings.push({
        pretention_id: p.id,
        code: 'pretention_sans_moyen',
        detail: `La prétention '${p.id}' (« ${p.objet.slice(0, 80)} ») n'est fondée par aucun moyen — vouée au rejet en l'état.`,
      });
      continue;
    }

    const only = attached.length === 1 ? attached[0] : undefined;
    if (only !== undefined) {
      const confidence = only.confidence;
      if (typeof confidence === 'number' && confidence < FRAGILE_CONFIDENCE_THRESHOLD) {
        warnings.push({
          pretention_id: p.id,
          code: 'moyen_unique_fragile',
          detail: `La prétention '${p.id}' repose sur le seul moyen '${only.id}' (confidence ${confidence.toFixed(2)} < ${FRAGILE_CONFIDENCE_THRESHOLD}) — envisager un moyen subsidiaire sur un autre terrain (concentration des moyens).`,
        });
      }
    }
  }

  return warnings;
}

/**
 * Dérive le champ TRANSITIONNEL `MoyenDraft.pretention` (texte libre consommé par
 * l'aval : prompts keyword/step2, aggregator) depuis les `objet` des prétentions
 * référencées. SSOT = `PretentionDraft.objet` ; ce texte n'est plus rédigé par le
 * LLM du planner. Refs inconnues ignorées (l'arbre est validé en amont par
 * `validatePretentionTree`).
 */
export function derivePretentionText(
  pretentionIds: readonly string[],
  pretentions: readonly PretentionDraft[],
): string {
  const byId = new Map(pretentions.map((p) => [p.id, p]));
  return pretentionIds
    .map((id) => byId.get(id)?.objet)
    .filter((objet): objet is string => typeof objet === 'string' && objet.trim().length > 0)
    .join(' ; ');
}
